"use client";

import Image from "next/image";
import { motion } from "framer-motion";
import { SITE } from "@/lib/constants";

export function AboutStory() {
  return (
    <section className="section-padding bg-background">
      <div className="mx-auto max-w-7xl">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-sm font-medium uppercase tracking-[0.2em] text-primary">
              Our Story
            </p>
            <h2 className="mt-3 font-playfair text-3xl font-bold leading-tight text-foreground md:text-4xl lg:text-5xl">
              A vision for exceptional private holidays
            </h2>
            <div className="mt-6 space-y-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              <p>
                {SITE.name} is part of the{" "}
                <span className="font-semibold text-foreground">Nest Villas</span>{" "}
                group, a growing luxury hospitality brand founded in{" "}
                <span className="font-semibold text-primary">2018</span> with a
                vision to create exceptional private holiday experiences across
                India.
              </p>
              <p>
                Nest Villas was built on a simple belief: a stay should be more
                than a place to sleep. It should be a setting for unforgettable
                experiences — a place where families reconnect, teams come
                together, celebrations feel special, and every holiday becomes a
                memory worth keeping.
              </p>
              <p>
                What began with thoughtfully curated private stays has grown into
                a luxury villa and boutique hospitality brand built around scenic
                destinations, handpicked properties, warm hospitality, and
                effortless guest experiences. Our properties are designed for
                guests who seek privacy, comfort, beauty, exclusivity, and a true
                sense of escape.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="relative grid grid-cols-2 gap-4"
          >
            <div className="relative aspect-[3/4] overflow-hidden rounded-2xl">
              <Image
                src="/images/exterior1.jpg"
                alt="Luxury villa exterior"
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 50vw, 25vw"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
            </div>
            <div className="relative mt-8 aspect-[3/4] overflow-hidden rounded-2xl">
              <Image
                src="/images/sunset-drinks-skydeck.jpg"
                alt="Sunset drinks on the sky deck"
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 50vw, 25vw"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="mt-16 rounded-2xl border border-primary/20 bg-card/60 p-8 md:p-10 lg:mt-20"
        >
          <p className="text-base leading-relaxed text-muted-foreground md:text-lg">
            At Nest Villas, we are in the business of{" "}
            <span className="font-semibold text-foreground">luxury holidays</span>{" "}
            and{" "}
            <span className="font-semibold text-foreground">
              elevated experiences
            </span>
            . From intimate family vacations and bespoke getaways to corporate
            luxe events, private celebrations, wellness retreats, and destination
            weddings, our spaces are created to bring people together in
            thoughtfully designed environments.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
